import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  BackgroundVariant,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  useGetWorkflowDefinitionQuery,
  useUpdateWorkflowDefinitionMutation,
  useGetWorkflowImpactQuery,
  StatusDefinition,
  TransitionDefinition,
  AuthorityRule,
  WorkflowDefinition,
  GovernanceGate,
  ApprovalType,
} from '@/redux/internal-services/workflowApi';
import {
  buildGraph,
  nodeTypes,
  edgeTypes,
  EDGE_COLOR,
} from '@/components/Workflow/workflowFlowchartGraph';
import { ROLE_OPTIONS, CATEGORY_OPTIONS, humanize } from '@/components/Workflow/workflowSettingsShared';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Chip } from '@/components/UI/Chip';
import { Switch } from '@/components/UI/Switch';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';

// ─────────────────────────── Sidebar helpers ──────────────────────────────

type Selection = { type: 'status'; code: string } | { type: 'transition'; code: string } | null;

const SidebarLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold text-ui-text2 mb-1">{children}</label>
);
const SidebarInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => (
  <input {...props} className={`${fieldClass({ size: 'sm' })} ${className}`} />
);
const SidebarSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className = '', ...props }) => (
  <select {...props} className={`${fieldClass({ size: 'sm' })} ${className}`} />
);
const SidebarTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = '', ...props }) => (
  <textarea {...props} className={`${fieldClass({ shape: 'box', size: 'xs' })} resize-y min-h-16 ${className}`} />
);
const ToggleSwitch: React.FC<{ checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, disabled, onChange, label }) => (
  <label className={`flex items-center gap-2 text-xs text-ui-text2 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} aria-label={label} />
    {label}
  </label>
);

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  draft: 'warning',
  published: 'success',
};

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const GATE_CONDITION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'required_data', label: 'Required data — field present' },
  { value: 'required_document', label: 'Document — files attached' },
  { value: 'task_completion', label: 'Required task — task complete' },
  { value: 'approval', label: 'Approval — requires sign-off' },
  { value: 'business_validation', label: 'Platform validation — built-in rule' },
];

// Adds a governance gate scoped to the transition currently open in the sidebar — target_ref is
// locked to that transition's code rather than free-typed, since this modal only ever opens from
// the transition panel (the flat, cross-transition version lives on WorkflowGovernancePage).
const AddGateModal: React.FC<{
  transitionCode: string;
  approvalTypes: ApprovalType[];
  onClose: () => void;
  onAdd: (g: GovernanceGate) => void;
}> = ({ transitionCode, approvalTypes, onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [conditionType, setConditionType] = useState(GATE_CONDITION_TYPES[0].value);
  const [classification, setClassification] = useState<'blocking' | 'warning'>('blocking');
  const [approvalTypeCode, setApprovalTypeCode] = useState('');
  const [approverRole, setApproverRole] = useState('');
  const [prohibitSelfApproval, setProhibitSelfApproval] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  const canSave = name.trim().length > 0;

  const selectApprovalType = (code: string) => {
    setApprovalTypeCode(code);
    const match = approvalTypes.find((a) => a.approval_type_code === code);
    if (match) {
      setApproverRole(match.approver_role);
      setProhibitSelfApproval(!match.self_approval_allowed);
    }
  };

  const save = () => {
    if (!canSave) return;
    onAdd({
      gate_code: slug(name),
      scope: 'transition',
      target_ref: transitionCode,
      classification,
      condition_type: conditionType,
      condition_config: conditionType === 'approval'
        ? { approval_type: approvalTypeCode || undefined, approver_role: approverRole || undefined, prohibit_self_approval: prohibitSelfApproval }
        : {},
      failure_message: failureMessage.trim() || undefined,
      description: name.trim(),
      is_optional: false,
    });
    onClose();
  };

  return (
    <Modal title="Add a governance condition" onClose={onClose} onSave={save} saveLabel="Add condition" saveDisabled={!canSave}>
      <div>
        <SidebarLabel>Condition name</SidebarLabel>
        <SidebarInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QA sign-off approval" />
      </div>
      <div>
        <SidebarLabel>Condition type</SidebarLabel>
        <SidebarSelect value={conditionType} onChange={(e) => setConditionType(e.target.value)}>
          {GATE_CONDITION_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </SidebarSelect>
      </div>
      <div>
        <SidebarLabel>Enforcement</SidebarLabel>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'blocking'} onChange={() => setClassification('blocking')} /> Blocking
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'warning'} onChange={() => setClassification('warning')} /> Warning
          </label>
        </div>
      </div>
      <div>
        <SidebarLabel>Failure message (shown to the user when blocked)</SidebarLabel>
        <SidebarInput value={failureMessage} onChange={(e) => setFailureMessage(e.target.value)} placeholder="e.g. Needs QA sign-off before this can move forward" />
      </div>
      {conditionType === 'approval' && (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Approval settings</p>
          {approvalTypes.length > 0 && (
            <div>
              <SidebarLabel>Approval type (optional)</SidebarLabel>
              <SidebarSelect value={approvalTypeCode} onChange={(e) => selectApprovalType(e.target.value)}>
                <option value="">Custom — set role manually below</option>
                {approvalTypes.map((a) => <option key={a.approval_type_code} value={a.approval_type_code}>{a.name}</option>)}
              </SidebarSelect>
            </div>
          )}
          <div>
            <SidebarLabel>Approver role</SidebarLabel>
            <SidebarSelect value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
            </SidebarSelect>
          </div>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={prohibitSelfApproval} onChange={(e) => setProhibitSelfApproval(e.target.checked)} /> Prohibit self-approval
          </label>
        </div>
      )}
    </Modal>
  );
};

// Cross-workflow impact analysis — "where else is this status used" — for the status detail
// panel. Calls the new GET /api/v1/platform/workflows/impact endpoint (Phase 1) so an admin can
// see the blast radius of a structural edit before making it.
const ImpactPanel: React.FC<{ statusCode: string; excludeDefinitionId: string }> = ({ statusCode, excludeDefinitionId }) => {
  const { data, isFetching } = useGetWorkflowImpactQuery({ status_code: statusCode, exclude_definition_id: excludeDefinitionId });
  const entries = data?.data ?? [];
  return (
    <div>
      <p className="text-xs font-semibold text-ui-text2 mb-1">Where else is this status used</p>
      {isFetching && <p className="text-xs text-ui-text3">Checking…</p>}
      {!isFetching && entries.length === 0 && <p className="text-xs text-ui-text3">Not used by any other workflow definition.</p>}
      {!isFetching && entries.length > 0 && (
        <ul className="space-y-1">
          {entries.map((e) => (
            <li key={`${e.definition_id}@${e.version}`} className="text-xs text-ui-text">
              {e.name} <span className="text-ui-text3">· v{e.version} · {e.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Transitions list/matrix — a table alongside the canvas so transitions can be selected without
// hunting for the right edge on the graph (mirrors the mockup's combined map + list view).
const TransitionsListPanel: React.FC<{
  transitions: TransitionDefinition[]; statuses: StatusDefinition[]; selectedCode?: string; onSelect: (code: string) => void;
}> = ({ transitions, statuses, selectedCode, onSelect }) => {
  const name = (code: string) => statuses.find((s) => s.status_code === code)?.name ?? code;
  return (
    <div className="w-72 border-r border-ui-border bg-ui-card shrink-0 overflow-y-auto">
      <div className="px-3 py-2 border-b border-ui-border">
        <p className="text-xs font-semibold text-ui-text2">{transitions.length} transition{transitions.length === 1 ? '' : 's'} defined</p>
      </div>
      <div>
        {transitions.map((t) => (
          <button
            key={t.transition_code}
            onClick={() => onSelect(t.transition_code)}
            className={`w-full text-left px-3 py-2 border-b border-ui-border text-xs transition ${
              selectedCode === t.transition_code ? 'bg-accent-soft' : 'hover:bg-ui-hover'
            }`}
          >
            <p className="text-ui-text">{name(t.source_status_code)} → {name(t.target_status_code)}</p>
            <p className="text-ui-text3 capitalize">{t.category.replace(/_/g, ' ')}</p>
          </button>
        ))}
        {transitions.length === 0 && <p className="px-3 py-4 text-xs text-ui-text3 text-center">No transitions yet.</p>}
      </div>
    </div>
  );
};

// Adds a brand-new Transition to an existing draft (post-wizard) — the wizard's step 4 only
// covers this at first-time creation; ongoing edits need the same capability here.
const AddTransitionModal: React.FC<{
  statuses: StatusDefinition[];
  existing: TransitionDefinition[];
  onClose: () => void;
  onAdd: (t: TransitionDefinition) => void;
}> = ({ statuses, existing, onClose, onAdd }) => {
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [requiresReason, setRequiresReason] = useState(false);

  const duplicate = source && target && existing.some(t => t.source_status_code === source && t.target_status_code === target);
  const canSave = source && target && actionLabel.trim() && !duplicate;

  const save = () => {
    if (!canSave) return;
    onAdd({
      transition_code: `${source}_to_${target}`,
      source_status_code: source,
      target_status_code: target,
      action_label: actionLabel.trim(),
      category: category as TransitionDefinition['category'],
      requires_reason: requiresReason,
      description: '',
    });
  };

  return (
    <Modal title="Add a new transition" onClose={onClose} onSave={save} saveLabel="Add transition" saveDisabled={!canSave}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <SidebarLabel>Source status</SidebarLabel>
          <SidebarSelect value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Select…</option>
            {statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
          </SidebarSelect>
        </div>
        <div>
          <SidebarLabel>Target status</SidebarLabel>
          <SidebarSelect value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Select…</option>
            {statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
          </SidebarSelect>
        </div>
      </div>
      <div>
        <SidebarLabel>Action label</SidebarLabel>
        <SidebarInput value={actionLabel} onChange={(e) => setActionLabel(e.target.value)} placeholder='e.g. "Send back for rework"' />
      </div>
      <div>
        <SidebarLabel>Category</SidebarLabel>
        <SidebarSelect value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{humanize(c)}</option>)}
        </SidebarSelect>
      </div>
      <label className="flex items-center gap-2 text-xs text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={requiresReason} onChange={(e) => setRequiresReason(e.target.checked)} />
        Requires reason
      </label>
      {duplicate && <p className="text-xs text-danger-text">A transition from this source to this target already exists.</p>}
      <p className="text-xs text-ui-text3">Authorized roles and governance gates can be set after adding, from the transition panel.</p>
    </Modal>
  );
};

// ─────────────────────────── Main page ────────────────────────────────────

type LocalDraft = {
  statuses: StatusDefinition[];
  transitions: TransitionDefinition[];
  authority_rules: AuthorityRule[];
  governance_gates: GovernanceGate[];
  approval_types: ApprovalType[];
};

const WorkflowFlowchartPage: React.FC = () => {
  const { t } = useTranslation();
  const { definitionId, version } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useGetWorkflowDefinitionQuery(
    { definition_id: definitionId!, version: Number(version) },
    { skip: !definitionId },
  );
  const [updateDraft, { isLoading: isSaving }] = useUpdateWorkflowDefinitionMutation();

  const def = data?.data;

  const [localDraft, setLocalDraft] = useState<LocalDraft>({ statuses: [], transitions: [], authority_rules: [], governance_gates: [], approval_types: [] });
  const [dirty, setDirty]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk]   = useState(false);
  const [showAddTransition, setShowAddTransition] = useState(false);
  const [showAddGate, setShowAddGate] = useState(false);

  useEffect(() => {
    if (def) setLocalDraft({
      statuses: [...def.statuses],
      transitions: [...def.transitions],
      authority_rules: [...(def.authority_rules ?? [])],
      governance_gates: [...(def.governance_gates ?? [])],
      approval_types: [...(def.approval_types ?? [])],
    });
  }, [def]);

  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);

  useEffect(() => {
    if (!def) return;
    const merged: WorkflowDefinition = { ...def, statuses: localDraft.statuses, transitions: localDraft.transitions };
    const { nodes: n, edges: e } = buildGraph(merged);
    setNodes(n);
    setEdges(e);
  }, [def, localDraft, setNodes, setEdges]);

  const [selection, setSelection] = useState<Selection>(null);

  const onNodeClick = useCallback((_evt: React.MouseEvent, node: Node) => {
    if (node.type === 'statusNode') setSelection({ type: 'status', code: node.id });
  }, []);

  const onEdgeClick = useCallback((_evt: React.MouseEvent, edge: Edge) => {
    setSelection({ type: 'transition', code: edge.id });
  }, []);

  const onPaneClick = useCallback(() => setSelection(null), []);

  const selectedStatus = useMemo(() =>
    selection?.type === 'status'
      ? localDraft.statuses.find(s => s.status_code === selection.code) ?? null
      : null,
    [selection, localDraft.statuses],
  );

  const selectedTransition = useMemo(() =>
    selection?.type === 'transition'
      ? localDraft.transitions.find(t => t.transition_code === selection.code) ?? null
      : null,
    [selection, localDraft.transitions],
  );

  const updateStatus = useCallback((field: keyof StatusDefinition, value: unknown) => {
    if (!selectedStatus) return;
    setLocalDraft(d => ({
      ...d,
      statuses: d.statuses.map(s => s.status_code === selectedStatus.status_code ? { ...s, [field]: value } : s),
    }));
    setDirty(true);
  }, [selectedStatus]);

  const updateTransition = useCallback((field: keyof TransitionDefinition, value: unknown) => {
    if (!selectedTransition) return;
    setLocalDraft(d => ({
      ...d,
      transitions: d.transitions.map(t => t.transition_code === selectedTransition.transition_code ? { ...t, [field]: value } : t),
    }));
    setDirty(true);
  }, [selectedTransition]);

  const deleteTransition = useCallback((transitionCode: string) => {
    setLocalDraft(d => ({
      ...d,
      transitions: d.transitions.filter(t => t.transition_code !== transitionCode),
      authority_rules: d.authority_rules.filter(r => !r.target_refs?.includes(transitionCode)),
      governance_gates: d.governance_gates.filter(g => g.target_ref !== transitionCode),
    }));
    setSelection(null);
    setDirty(true);
  }, []);

  const gatesForTransition = useCallback((transitionCode: string) =>
    localDraft.governance_gates.filter(g => g.target_ref === transitionCode),
    [localDraft.governance_gates],
  );

  const addGate = useCallback((gate: GovernanceGate) => {
    setLocalDraft(d => ({ ...d, governance_gates: [...d.governance_gates, gate] }));
    setDirty(true);
    setShowAddGate(false);
  }, []);

  const removeGate = useCallback((gateCode: string) => {
    setLocalDraft(d => ({ ...d, governance_gates: d.governance_gates.filter(g => g.gate_code !== gateCode) }));
    setDirty(true);
  }, []);

  const addTransition = useCallback((tr: TransitionDefinition) => {
    setLocalDraft(d => ({ ...d, transitions: [...d.transitions, tr] }));
    setSelection({ type: 'transition', code: tr.transition_code });
    setDirty(true);
    setShowAddTransition(false);
  }, []);

  const authorityRuleFor = useCallback((transitionCode: string) =>
    localDraft.authority_rules.find(r => r.target_refs?.includes(transitionCode)),
    [localDraft.authority_rules],
  );

  const setTransitionRoles = useCallback((transitionCode: string, roles: string[]) => {
    setLocalDraft(d => {
      const existing = d.authority_rules.find(r => r.target_refs?.includes(transitionCode));
      if (existing) {
        return {
          ...d,
          authority_rules: d.authority_rules.map(r => r === existing ? { ...r, allowed_roles: roles } : r),
        };
      }
      const newRule: AuthorityRule = {
        rule_code: `${transitionCode}_authority`,
        authority_type: 'transition',
        target_refs: [transitionCode],
        allowed_roles: roles,
      };
      return { ...d, authority_rules: [...d.authority_rules, newRule] };
    });
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!def || !dirty) return;
    setSaveError('');
    try {
      await updateDraft({
        definition_id: def.definition_id,
        version: def.version,
        body: {
          ...def,
          statuses: localDraft.statuses,
          transitions: localDraft.transitions,
          authority_rules: localDraft.authority_rules,
          governance_gates: localDraft.governance_gates,
          approval_types: localDraft.approval_types,
        },
      }).unwrap();
      setDirty(false);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setSaveError(err?.data?.message ?? 'Save failed.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-dvh bg-ui">
        <LoadingState label="Loading…" />
      </div>
    );
  }

  if (!def) {
    return (
      <div className="flex items-center justify-center h-dvh bg-ui">
        <EmptyState title={t('platform.workflowFlowchartNotFound')} />
      </div>
    );
  }

  const isDraft = def.status === 'draft';

  return (
    <div className="flex flex-col h-dvh bg-ui">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-ui-border bg-ui-card shrink-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="link" size="xs" onClick={() => navigate(`/platform/workflows/${definitionId}/${version}`)}>
            ← Detail
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ui-text">{def.name}</span>
              <Badge variant={STATUS_VARIANT[def.status] ?? 'neutral'} className="font-semibold capitalize">{def.status}</Badge>
            </div>
            <p className="text-xs text-ui-text3 font-mono">{def.definition_id} v{version}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saveError && <p className="text-xs text-danger-text">{saveError}</p>}
          {saveOk   && <p className="text-xs text-success-text">Saved!</p>}
          {isDraft && (
            <Button size="sm" onClick={() => setShowAddTransition(true)}>
              + New transition
            </Button>
          )}
          {isDraft && dirty && (
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          )}
          {isDraft && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/platform/workflows/${definitionId}/${version}/edit`)}>
              Open Editor
            </Button>
          )}
        </div>
      </div>

      {/* List + Canvas + sidebar */}
      <div className="flex flex-1 min-h-0">
        <TransitionsListPanel
          transitions={localDraft.transitions}
          statuses={localDraft.statuses}
          selectedCode={selection?.type === 'transition' ? selection.code : undefined}
          onSelect={(code) => setSelection({ type: 'transition', code })}
        />
        <div className="flex-1" style={{ background: '#0d1117' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            nodeTypes={nodeTypes as any}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            edgeTypes={edgeTypes as any}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.2}
            maxZoom={2}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1e2d3d" />
            <Controls style={{ background: '#1e293b', borderColor: '#334155', color: '#94a3b8' }} />
            <MiniMap
              style={{ background: '#0f172a', border: '1px solid #334155' }}
              nodeColor={(n) => {
                const s = (n.data as { status?: StatusDefinition })?.status;
                if (!s) return '#374151';
                return s.lifecycle_role === 'entry' ? '#3b82f6' : s.lifecycle_role === 'terminal' ? '#ef4444' : '#475569';
              }}
              maskColor="rgba(0,0,0,0.6)"
            />
            <Panel position="top-left" style={{ margin: '8px' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '6px 10px', background: 'rgba(15,23,42,0.8)', borderRadius: 8, border: '1px solid #1e2d3d' }}>
                {Object.entries(EDGE_COLOR).map(([cat, color]) => (
                  <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: '#94a3b8' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 2, background: color, borderRadius: 1 }} />
                    {cat.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {/* Right sidebar */}
        {selection && (
          <div className="w-64 border-l border-ui-border bg-ui-card shrink-0 overflow-y-auto">
            <div className="p-4 space-y-4">
              {selectedStatus && (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-ui-text">Status</h3>
                    <IconButton size="xs" label="Close panel" onClick={() => setSelection(null)}>×</IconButton>
                  </div>
                  <div><SidebarLabel>Name</SidebarLabel>
                    <SidebarInput value={selectedStatus.name} disabled={!isDraft} onChange={e => updateStatus('name', e.target.value)} />
                  </div>
                  <div><SidebarLabel>Code</SidebarLabel>
                    <SidebarInput value={selectedStatus.status_code} disabled className="font-mono text-xs" />
                  </div>
                  <div><SidebarLabel>Lifecycle Role</SidebarLabel>
                    <SidebarSelect value={selectedStatus.lifecycle_role} disabled={!isDraft} onChange={e => updateStatus('lifecycle_role', e.target.value)}>
                      <option value="entry">Entry</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="terminal">Terminal</option>
                    </SidebarSelect>
                  </div>
                  <div><SidebarLabel>Operational Type</SidebarLabel>
                    <SidebarSelect value={selectedStatus.operational_type} disabled={!isDraft} onChange={e => updateStatus('operational_type', e.target.value)}>
                      <option value="normal">Normal</option><option value="on_hold">On Hold</option>
                      <option value="closed">Closed</option><option value="cancelled">Cancelled</option>
                      <option value="rejected">Rejected</option>
                    </SidebarSelect>
                  </div>
                  <div><SidebarLabel>Description</SidebarLabel>
                    <SidebarTextarea value={selectedStatus.description ?? ''} disabled={!isDraft} rows={3} onChange={e => updateStatus('description', e.target.value)} />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-ui-text2 cursor-pointer">
                    <input type="checkbox" checked={selectedStatus.is_optional ?? false} disabled={!isDraft} onChange={e => updateStatus('is_optional', e.target.checked)} />
                    Optional
                  </label>
                  <ImpactPanel statusCode={selectedStatus.status_code} excludeDefinitionId={def.definition_id} />
                  <div>
                    <p className="text-xs font-semibold text-ui-text2 mb-1">Transitions from here</p>
                    {localDraft.transitions.filter(t => t.source_status_code === selectedStatus.status_code).length === 0
                      ? <p className="text-xs text-ui-text3">None</p>
                      : localDraft.transitions
                          .filter(t => t.source_status_code === selectedStatus.status_code)
                          .map(t => (
                            <button key={t.transition_code} onClick={() => setSelection({ type: 'transition', code: t.transition_code })}
                              className="block w-full text-left text-xs text-accent-text hover:underline mb-1">
                              → {def.statuses.find(s => s.status_code === t.target_status_code)?.name ?? t.target_status_code}
                              <span className="text-ui-text3 ml-1">({t.action_label})</span>
                            </button>
                          ))
                    }
                  </div>
                </>
              )}
              {selectedTransition && (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-ui-text">Transition</h3>
                    <IconButton size="xs" label="Close panel" onClick={() => setSelection(null)}>×</IconButton>
                  </div>
                  <div><SidebarLabel>Code</SidebarLabel>
                    <SidebarInput value={selectedTransition.transition_code} disabled className="font-mono text-xs" />
                  </div>
                  <div><SidebarLabel>From</SidebarLabel>
                    <p className="text-sm text-ui-text">{def.statuses.find(s => s.status_code === selectedTransition.source_status_code)?.name ?? selectedTransition.source_status_code}</p>
                  </div>
                  <div><SidebarLabel>Target</SidebarLabel>
                    <SidebarSelect value={selectedTransition.target_status_code} disabled={!isDraft} onChange={e => updateTransition('target_status_code', e.target.value)}>
                      {localDraft.statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
                    </SidebarSelect>
                  </div>
                  <div><SidebarLabel>Action Label</SidebarLabel>
                    <SidebarInput value={selectedTransition.action_label} disabled={!isDraft} onChange={e => updateTransition('action_label', e.target.value)} />
                  </div>
                  <div><SidebarLabel>Category</SidebarLabel>
                    <SidebarSelect value={selectedTransition.category} disabled={!isDraft} onChange={e => updateTransition('category', e.target.value)}>
                      {['standard','backward','skip','hold','resume','reopen','cancel','reject','corrective_loop'].map(c => (
                        <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                      ))}
                    </SidebarSelect>
                  </div>
                  <div><SidebarLabel>Description</SidebarLabel>
                    <SidebarTextarea value={selectedTransition.description ?? ''} disabled={!isDraft} rows={3} onChange={e => updateTransition('description', e.target.value)} />
                  </div>
                  <div className="card-surface p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <SidebarLabel>Governance gates</SidebarLabel>
                      <span className="text-xs text-ui-text3">{gatesForTransition(selectedTransition.transition_code).length}</span>
                    </div>
                    {gatesForTransition(selectedTransition.transition_code).map((g) => (
                      <div key={g.gate_code} className="flex items-center justify-between gap-2">
                        <p className="text-xs text-ui-text truncate">{g.description || g.gate_code}</p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant={g.classification === 'blocking' ? 'danger' : 'warning'} className="text-micro uppercase">{g.classification}</Badge>
                          {isDraft && (
                            <IconButton size="xs" label="Remove condition" className="hover:text-danger-text" onClick={() => removeGate(g.gate_code)}>×</IconButton>
                          )}
                        </div>
                      </div>
                    ))}
                    {gatesForTransition(selectedTransition.transition_code).length === 0 && (
                      <p className="text-xs text-ui-text3">No governance conditions yet.</p>
                    )}
                    {isDraft && (
                      <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAddGate(true)}>
                        + Add condition
                      </Button>
                    )}
                  </div>
                  <div>
                    <SidebarLabel>Authorized roles</SidebarLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {ROLE_OPTIONS.map((r) => {
                        const rule = authorityRuleFor(selectedTransition.transition_code);
                        const checked = rule?.allowed_roles.includes(r) ?? false;
                        return (
                          <Chip
                            key={r}
                            selected={checked}
                            disabled={!isDraft}
                            onClick={() => {
                              const current = rule?.allowed_roles ?? [];
                              const next = checked ? current.filter(x => x !== r) : [...current, r];
                              setTransitionRoles(selectedTransition.transition_code, next);
                            }}
                          >
                            {humanize(r)}
                          </Chip>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-xs text-ui-text3">No roles selected means any tenant-assigned role may perform it.</p>
                  </div>
                  <ToggleSwitch
                    checked={selectedTransition.requires_reason ?? false}
                    disabled={!isDraft}
                    onChange={(v) => updateTransition('requires_reason', v)}
                    label="Require reason"
                  />
                  {!isDraft && <p className="text-xs text-ui-text3 italic">Published workflows are read-only.</p>}
                  {isDraft && (
                    <div className="space-y-2 pt-1">
                      {dirty && (
                        <Button size="sm" className="w-full" onClick={handleSave} disabled={isSaving}>
                          {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                      )}
                      <Button
                        variant="destructive-ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => deleteTransition(selectedTransition.transition_code)}
                      >
                        Delete transition
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {showAddTransition && (
        <AddTransitionModal
          statuses={localDraft.statuses}
          existing={localDraft.transitions}
          onClose={() => setShowAddTransition(false)}
          onAdd={addTransition}
        />
      )}
      {showAddGate && selectedTransition && (
        <AddGateModal
          transitionCode={selectedTransition.transition_code}
          approvalTypes={localDraft.approval_types}
          onClose={() => setShowAddGate(false)}
          onAdd={addGate}
        />
      )}
    </div>
  );
};

export default WorkflowFlowchartPage;
